import { ConnectError } from '@bufbuild/connect';
import { Modal, notification } from 'antd';
import { MessageDescriptor, useIntl } from 'react-intl';

import { useAppDispatch } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import { userModel } from '@/entities/user';

type OrganizationStatusThunk =
  | typeof userModel.thunks.markOrganizationAsDSO
  | typeof userModel.thunks.unmarkOrganizationAsDSO
  | typeof userModel.thunks.unmarkOrganizationAsDistributor;

type UseOrganizationStatusModalParams = {
  messages: {
    content: MessageDescriptor;
    success: MessageDescriptor;
    title: MessageDescriptor;
  };
  thunk: OrganizationStatusThunk;
  danger?: boolean;
};

/**
 * Confirmation modal for the DSO and distributor status switches. They take the
 * same request, refresh the same organization in the store and report the same
 * way — only the wording and how destructive they are differ.
 */
export const useOrganizationStatusModal = (
  params: UseOrganizationStatusModalParams,
) => {
  const { messages, thunk, danger = false } = params;

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  return (organizationID: string) => {
    Modal.confirm({
      title: formatMessage(messages.title),
      content: formatMessage(messages.content),
      okText: formatMessage({
        id: 'global.confirm',
        defaultMessage: 'Confirm',
      }),
      cancelText: formatMessage({
        id: 'global.cancel',
        defaultMessage: 'Cancel',
      }),
      okButtonProps: { danger },
      onOk: async () => {
        try {
          const { Organization } = await dispatch(
            thunk({ OrganizationID: organizationID }),
          ).unwrap();

          if (Organization?.ID) {
            dispatch(
              organizationModel.actions.setNewestOrganization(Organization),
            );
          }

          notification.success({
            message: formatMessage(messages.success),
          });
        } catch (error) {
          notification.error({
            message:
              error instanceof ConnectError
                ? error.message
                : formatMessage({
                    id: 'global.error.somethingWentWrong',
                    defaultMessage: 'Something went wrong',
                  }),
          });
        }
      },
    });
  };
};
